import { SANDBOX_PRESET_TAG as T } from '../presetTags'
import { SANDBOX_PRESET_BODY_CSS, type SandboxComponentPreset } from '../../sandboxPresetTypes'

/**
 * An accessible single-field pattern with a client-side **email check** using `RegExp`. It is demo-grade:
 * use a library or server-side validation in production. `aria-invalid` toggles with the result. The
 * focus ring stays on `:focus`.
 */
const shell = SANDBOX_PRESET_BODY_CSS

const preset: SandboxComponentPreset = {
  id: 'email-regex',
  category: 'forms',
  title: 'Email (regex)',
  description: 'Label, focus ring, and Continue gated by a simple email regex.',
  tags: [T.form, T.email, T.validation, T.a11y],
  html: `<h1 data-testid="preset-email-regex-heading">Sign in</h1>

<!-- for/id pairs: clicking the label focuses the input -->
<form class="stack" novalidate>
  <label for="email">Email</label>
  <!-- type=email: helpful keyboard/widgets; we still validate shape in JS with a regex -->
  <input id="email" name="email" type="email" placeholder="you@example.com" autocomplete="off" aria-describedby="hint" />
  <p id="hint" class="hint">Uses a small regex — not exhaustive.</p>
  <button type="button" id="submit">Continue</button>
  <p id="msg" class="msg" role="status"></p>
</form>`,
  css: `${shell}

h1 {
  color: var(--nogrok-preview-primary);
  font-size: 1.1rem;
  margin: 0 0 1rem;
}

.stack {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-width: 16rem;
}

label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--nogrok-preview-on-surface-muted);
}

.hint {
  margin: 0;
  font-size: 0.7rem;
  color: var(--nogrok-preview-on-surface-variant);
  line-height: 1.3;
}

input {
  font: inherit;
  padding: 0.5rem 0.65rem;
  border: 1px solid var(--nogrok-preview-outline);
  border-radius: 6px;
  background: var(--nogrok-preview-surface-container);
  color: var(--nogrok-preview-on-surface);
}

/* High-contrast ring — no outline: none without a replacement */
input:focus {
  outline: 2px solid var(--nogrok-preview-primary);
  outline-offset: 1px;
}

/* Added via JS after Continue — avoids fighting native :user-invalid on type=email */
input.is-invalid {
  border-color: #ff8a8a;
}

#submit {
  margin-top: 0.25rem;
  font: inherit;
  padding: 0.5rem;
  border-radius: 6px;
  border: none;
  background: var(--nogrok-preview-primary);
  color: var(--nogrok-preview-on-primary);
  font-weight: 700;
  cursor: pointer;
}

.msg {
  margin: 0;
  font-size: 0.85rem;
  min-height: 1.25em;
}

.msg.ok {
  color: #7dffb0;
}

.msg.err {
  color: #ff8a8a;
}`,
  js: `// Practical-enough shape check: local@domain.tld — not RFC-complete (unicode, quotes, etc.).
const EMAIL_RE = /^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$/

const inp = document.getElementById('email')
const msg = document.getElementById('msg')

function paint(ok, text) {
  if (!(inp instanceof HTMLInputElement)) return
  inp.classList.toggle('is-invalid', !ok)
  inp.setAttribute('aria-invalid', ok ? 'false' : 'true')
  if (!msg) return
  msg.className = 'msg' + (ok ? ' ok' : ' err')
  msg.textContent = text
}

document.getElementById('submit')?.addEventListener('click', () => {
  const val = inp instanceof HTMLInputElement ? inp.value.trim() : ''
  if (!val) {
    paint(false, 'Enter an email.')
    return
  }
  if (!EMAIL_RE.test(val)) {
    paint(false, 'Does not match the demo pattern (expect user@host.tld).')
    return
  }
  paint(true, 'Looks valid.')
})

inp?.addEventListener('input', () => {
  if (msg && inp instanceof HTMLInputElement) {
    msg.className = 'msg'
    msg.textContent = ''
    inp.classList.remove('is-invalid')
    inp.setAttribute('aria-invalid', 'false')
  }
})`,
}

export default preset
